import { Link } from 'react-router-dom';
import { Clock, Eye, Bookmark, BookmarkCheck } from 'lucide-react';
import { useReadingList } from '../../context/AppContext';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import styles from './ArticleCard.module.css';

export default function ArticleCard({ 
  article, 
  variant = 'default', // default, featured, compact, horizontal
  showImage = true,
  showExcerpt = true,
  showAuthor = true,
  showCategory = true,
  showMeta = true,
  imageAspect = '16/9',
  className = '',
}) {
  const { isInReadingList, addToReadingList, removeFromReadingList } = useReadingList();
  const saved = isInReadingList(article.id);

  const formatDate = (dateString) => {
    const date = parseISO(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true, locale: es });
    }
    return format(date, "d 'de' MMMM, yyyy", { locale: es });
  };

  const formatViews = (views) => {
    if (views >= 1000000) {
      return (views / 1000000).toFixed(1) + 'M';
    }
    if (views >= 1000) {
      return (views / 1000).toFixed(1) + 'K';
    }
    return views.toString();
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (saved) {
      removeFromReadingList(article.id);
    } else {
      addToReadingList(article);
    }
  };

  const cardClass = `${styles.card} ${styles[variant]} ${className}`;

  return (
    <article className={cardClass}>
      {showImage && article.image && (
        <Link to={`/articulo/${article.slug}`} className={styles.imageWrapper}>
          <img 
            src={article.image} 
            alt={article.title}
            className={styles.image}
            loading="lazy"
            style={{ aspectRatio: imageAspect }}
          />
          {variant === 'featured' && showCategory && article.category && (
            <span 
              className={styles.categoryBadge}
              style={{ backgroundColor: article.category.color }}
            >
              {article.category.name}
            </span>
          )}
        </Link>
      )}

      <div className={styles.content}>
        {variant !== 'featured' && showCategory && article.category && (
          <Link 
            to={`/seccion/${article.category.slug}`}
            className={styles.category}
            style={{ color: article.category.color }}
          >
            {article.category.name}
          </Link>
        )}

        <h3 className={styles.title}>
          <Link to={`/articulo/${article.slug}`}>
            {article.title}
          </Link>
        </h3>

        {showExcerpt && article.excerpt && variant !== 'compact' && (
          <p className={styles.excerpt}>{article.excerpt}</p>
        )}

        {(showMeta || showAuthor) && (
          <div className={styles.meta}>
            {showAuthor && article.author && (
              <Link 
                to={`/autor/${article.author.id}`} 
                className={styles.author}
              >
                {article.author.avatar ? (
                  <img
                    src={article.author.avatar}
                    alt={article.author.name}
                    className={styles.authorAvatar}
                  />
                ) : (
                  <span className={styles.authorAvatarFallback}>
                    {(article.author.name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                {article.author.name}
              </Link>
            )}
            
            {showMeta && (
              <div className={styles.metaInfo}>
                <span className={styles.date}>
                  <Clock size={14} />
                  {formatDate(article.publishedAt)}
                </span>
                {article.views > 0 && (
                  <span className={styles.views}>
                    <Eye size={14} />
                    {formatViews(article.views)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <button 
          className={`${styles.saveButton} ${saved ? styles.saved : ''}`}
          onClick={handleSaveClick}
          aria-label={saved ? 'Quitar de guardados' : 'Guardar artículo'}
        >
          {saved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
        </button>
      </div>
    </article>
  );
}
